import { apiClientV2 } from "@/services/api/apiClient"
import { useQuery } from "@tanstack/react-query"
import type { MasterDataEntityStats, MasterDataStats } from "./types/masterData"

function normalizeEntity(
  raw: Partial<MasterDataEntityStats> | undefined,
): MasterDataEntityStats {
  return {
    total: raw?.total ?? 0,
    missingData: raw?.missingData ?? 0,
    waiting: raw?.waiting ?? 0,
    needsReview: raw?.needsReview ?? 0,
    complete: raw?.complete ?? 0,
  }
}

export const masterDataStatsApiCalls = {
  /** GET /material-compliance/master-data/stats — per-entity counts.
   * Partial payloads normalize to zeros; request FAILURES propagate so the
   * caller's isError is real (no false-green zeros on the start page). */
  getMasterDataStats: async (): Promise<MasterDataStats> => {
    const raw = await apiClientV2
      .get("/material-compliance/master-data/stats")
      .then(
        (r) =>
          r.data as Partial<
            Record<keyof MasterDataStats, Partial<MasterDataEntityStats>>
          >,
      )

    return {
      suppliers: normalizeEntity(raw.suppliers),
      articles: normalizeEntity(raw.articles),
      products: normalizeEntity(raw.products),
      substances: normalizeEntity(raw.substances),
      customers: normalizeEntity(raw.customers),
    }
  },
}

/**
 * `enabled` gates the request itself, the same way useDataGapsStats does: the
 * endpoint sits behind MC_MASTER_DATA_VIEW, so an ungated query 403s for every
 * member without that permission.
 *
 * It is also part of the key: a disabled query still serves whatever its key
 * cached, so a member whose permission is revoked mid-session would keep
 * seeing the last counts. The disabled key never fetched, so it has nothing
 * to serve. Invalidations use the three-part prefix and still match.
 */
export function useMasterDataStats({
  enabled = true,
}: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["mc", "master-data", "stats", { enabled }],
    queryFn: masterDataStatsApiCalls.getMasterDataStats,
    enabled,
  })
}
